import React, { useState, useMemo } from 'react';
import { AlertTriangle, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { format } from 'date-fns';

const urgencyStyle = (days) => {
  if (days <= 14) return { bg: 'bg-red-50 border-red-200', text: 'text-red-600', badge: 'bg-red-100 text-red-700' };
  if (days <= 30) return { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-600', badge: 'bg-orange-100 text-orange-700' };
  if (days <= 90) return { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' };
  return { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-600', badge: 'bg-gray-100 text-gray-700' };
};

const FILTERS = [
  { key: 'all',      label: 'All',       chip: 'bg-violet-100 text-violet-700 border-violet-300' },
  { key: 'Passport', label: 'Passport',  chip: 'bg-blue-100 text-blue-700 border-blue-300' },
  { key: 'Visa',     label: 'Visa',      chip: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { key: 'Labour',   label: 'Labour',    chip: 'bg-amber-100 text-amber-700 border-amber-300' },
  { key: 'Emirates ID', label: 'Emirates ID', chip: 'bg-rose-100 text-rose-700 border-rose-300' },
];

const ExpiringDocumentsCard = ({ expiringDocs }) => {
  const [filter, setFilter] = useState('all');

  const counts = useMemo(() => {
    const c = { all: 0, Passport: 0, Visa: 0, Labour: 0, 'Emirates ID': 0 };
    (expiringDocs || []).forEach(d => {
      c.all += 1;
      const base = (d.document_type || '').split(' (')[0];
      if (c[base] != null) c[base] += 1;
    });
    return c;
  }, [expiringDocs]);

  const filtered = useMemo(() => {
    if (filter === 'all') return expiringDocs || [];
    return (expiringDocs || []).filter(d => (d.document_type || '').split(' (')[0] === filter);
  }, [expiringDocs, filter]);

  const critical = filtered.filter(d => d.days_until_expiry <= 30).length;

  return (
    <Card data-testid="expiring-docs-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-500" />
            Expiring Documents
          </span>
          {critical > 0 && (
            <Badge className="bg-red-100 text-red-700 text-xs">{critical} critical</Badge>
          )}
        </CardTitle>
        <div className="flex flex-wrap gap-1.5 pt-2">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                filter === f.key
                  ? f.chip + ' font-semibold'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`exp-filter-${f.key.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {f.label} <span className="ml-0.5 opacity-70">{counts[f.key] || 0}</span>
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <p className="text-gray-500 text-center py-4 text-sm">
            {filter === 'all' ? 'No documents expiring soon' : `No ${filter} expiries`}
          </p>
        ) : (
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {filtered.map((doc, index) => {
              const style = urgencyStyle(doc.days_until_expiry);
              return (
                <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${style.bg}`}
                  data-testid={`expiring-doc-${index}`}>
                  <div>
                    <p className="font-medium text-sm">{doc.employee_name}</p>
                    <p className="text-xs text-gray-600">{doc.employee_code ? `${doc.employee_code} · ` : ''}{doc.document_type}: {doc.document_number}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={`text-xs ${style.badge}`}>
                      {doc.days_until_expiry}d
                    </Badge>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {format(new Date(doc.expiry_date), 'dd MMM yyyy')}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ExpiredDocumentsCard = ({ expiredDocs }) => {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          Expired Documents
          {expiredDocs?.length > 0 && (
            <Badge className="bg-red-100 text-red-700 text-xs ml-auto">{expiredDocs.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!expiredDocs || expiredDocs.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No expired documents</p>
        ) : (
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {expiredDocs.slice(0, 10).map((doc, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200"
                data-testid={`expired-doc-${index}`}>
                <div>
                  <p className="font-medium text-sm">{doc.employee_name}</p>
                  <p className="text-xs text-gray-600">{doc.employee_code} · {doc.document_type}: {doc.document_number}</p>
                </div>
                <div className="text-right">
                  <Badge className="bg-red-100 text-red-700 text-xs">
                    {doc.days_expired}d ago
                  </Badge>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {format(new Date(doc.expiry_date), 'dd MMM yyyy')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export { ExpiringDocumentsCard, ExpiredDocumentsCard };
